/*
 * TSS Skin Quiz, Persona Rules Engine (v2, aligned with "TSS_Quiz_Persona_Table - for aditya.xlsx")
 * ------------------------------------------------------------
 * Deterministic mapper from quiz answers → persona + products + routine.
 *
 * KEY CHANGES FROM v1:
 *  - LPP removed from quiz output entirely (FQ9 resolved: no LPP)
 *  - RCF frequency buckets: persistent | frequent | periodic | rare
 *      persistent/frequent → RCF 2x/day (oily/combo/normal), RCF 1x/day PM + BSC AM (dry)
 *      periodic            → RCF 1x/day PM + BSC AM
 *      rare                → BSC daily + RCF SOS
 *  - Texture+acne with persistent acne → RCF PM + BSC AM (USDC daily is too harsh; NCO-006)
 *  - Texture+acne with frequent acne   → RCF PM + USDC AM (NCO-005)
 *  - Dry skin + texture → USDC alternate days + BSC other days (DRY-004)
 *  - Dry skin + any RCF → always spot/SOS only (M3)
 *  - Fungal Acne / Rosacea / Irritation → overrides preserved
 *  - Pure maintenance: oily → USDC+BSC+BRGM+SS, others → BSC+BRGM/BRIM+SS
 *
 * Source of truth: Persona Mapping Table + Product Logic + Modifier Rules sheets.
 * Test by spot-checking 3+ personas against the sheet after any edit.
 */
(function () {
  'use strict';

  // =========================================================================
  // ⚠️ TEMPORARY: out-of-stock product suppression.
  // While a product's code is in this list, it is stripped from EVERY routine
  // (product cards, AM/PM steps, bundle price, AI copy, analytics). To bring a
  // product back, remove its code from this array.
  //   SS = Clear Protect Gel Sunscreen, was out of stock (added 2026-07-03).
  //        Restored 2026-08-04, back in stock.
  // =========================================================================
  var OUT_OF_STOCK = [];

  // --- Product metadata (handles from Shopify admin) -----------------------
  var PRODUCTS = {
    RCF:   { handle: 'rapid-clear-facewash',                   name: 'Rapid Clear Facewash',           short: 'RCF',   active: 'Micronized BPO' },
    BSC:   { handle: 'barrier-soothing-cleanser',              name: 'Barrier Soothing Cleanser',      short: 'BSC',   active: 'Glycerin + Ceramides' },
    USDC:  { handle: 'ultra-smooth-daily-facewash',            name: 'Ultra Smooth Daily Cleanser',    short: 'USDC',  active: 'AHA + BHA' },
    EMC:   { handle: 'effortless-melting-cleanser',            name: 'Effortless Melting Cleanser',    short: 'EMC',   active: 'Makeup dissolving' },
    BRGM:  { handle: 'barrier-repair-gel-moisturiser',         name: 'Barrier Repair Gel Moisturiser', short: 'BRGM',  active: 'Ceramides + Niacinamide' },
    BRIM:  { handle: 'barrier-repair-intense-moisturizer',     name: 'Barrier Repair Intense Moisturiser', short: 'BRIM', active: 'Ceramides + HA + Squalane' },
    SFARS: { handle: 'spot-fade-advanced-repair-serum',        name: 'SpotFade Advanced Repair Serum', short: 'SFARS', active: 'Azelaic Acid + Niacinamide' },
    SS:    { handle: 'clear-protect-soothing-gel-sunscreen-50ml', name: 'Clear Protect Gel Sunscreen SPF 50+', short: 'SS', active: 'Hybrid UV filters' }
  };

  // --- Q2 option → internal concern bucket ---------------------------------
  var CONCERN_MAP = {
    'red_painful_pimples':    'active_acne',
    'pus_filled_acne':        'active_acne',
    'large_cystic_pimples':   'active_acne',
    'small_white_red_pimples':'active_acne',
    'blackheads':             'texture',
    'whiteheads':             'texture',
    'uneven_texture':         'texture',
    'open_pores':             'texture',
    'post_acne_marks':        'marks',
    'maintenance':            'maintenance',
    'irritated_sensitive':    'irritation',
    'fungal_acne':            'fungal_acne',
    'rosacea':                'rosacea'
  };

  function bucketsFrom(concerns) {
    var set = {};
    (concerns || []).forEach(function (c) {
      var b = CONCERN_MAP[c];
      if (b) set[b] = true;
    });
    return set;
  }

  // Q3 user answer → internal freq bucket
  // Per Aditya sheet: Almost always = Persistent, Every now & then = Frequent,
  //                   Once a month = Periodic, Rarely = Rare
  var FREQ_MAP = {
    'almost_always':   'persistent',
    'every_now_then':  'frequent',
    'once_a_month':    'periodic',
    'rarely':          'rare'
  };

  // ================================================================
  //  compute(answers), main entry point
  // ================================================================
  function compute(answers) {
    var skin = answers.skin_type || 'combination';
    if (skin === 'not_sure') skin = 'combination'; // M9

    var b = bucketsFrom(answers.concerns);
    var freq = FREQ_MAP[answers.acne_freq] || null;
    var moist = (skin === 'dry') ? 'BRIM' : 'BRGM'; // M2
    var usesMakeup = (answers.makeup === 'yes_daily' || answers.makeup === 'sometimes');

    // ---- PRIORITY 1: Fungal Acne / Rosacea OVERRIDES ---
    if (b.fungal_acne || b.rosacea) {
      var isfungal = !!b.fungal_acne;
      return finalize({
        persona_id: 'OVERRIDE-' + skin.toUpperCase() + '-' + (isfungal ? 'FUNGAL' : 'ROSACEA'),
        products: dedupe(['BSC','SFARS', moist, 'SS']),
        am: ['BSC','SFARS', moist, 'SS'],
        pm: [('BSC'), 'SFARS', moist],
        active_count: 1,
        intro_delay: 'Single active (SFARS), no stagger needed. Introduce gradually: every other day for Week 1, then daily.',
        headline: isfungal ? 'Your Routine for Fungal Acne' : 'Your Routine for Rosacea',
        concern_summary: isfungal
          ? "Fungal acne (Malassezia) needs anti-inflammatory + anti-microbial care, not BPO or harsh exfoliants. That's what this routine gives you."
          : "Rosacea responds to gentle, anti-inflammatory care. We've stripped out anything that could flare your skin and leaned into what calms it.",
        why_concerns: ['fungal_rosacea'],
        skin: skin, buckets: b, freq: freq, answers: answers,
        usesMakeup: usesMakeup
      });
    }

    // ---- PRIORITY 2: Irritation OVERRIDE (no actives) ---
    // Fires ONLY when irritation has no companion treatable concern. If the
    // user flagged irritation alongside active acne, texture, or marks, we
    // fall through to the regular cluster logic below and stamp `barrier_first`
    // on the result so the PDP shows a 2-week barrier-strengthening note.
    // This lets a sensitive user with treatable concerns still see the
    // relevant actives, with proper guidance on how to introduce them.
    var hasTreatable = !!(b.active_acne || b.texture || b.marks);
    if (b.irritation && !hasTreatable) {
      return finalize({
        persona_id: 'OVERRIDE-' + skin.toUpperCase() + '-IRRITATION',
        products: dedupe(['BSC', moist, 'SS']),
        am: ['BSC', moist, 'SS'],
        pm: ['BSC', moist],
        active_count: 0,
        intro_delay: "No actives for now. Use this calming routine daily until your skin feels settled, usually 2–4 weeks. Then we can add actives slowly, one at a time.",
        headline: 'Your Routine for Sensitive, Irritated Skin',
        concern_summary: "Your barrier needs to heal before any actives come in. This routine is built to calm, hydrate, and protect, and nothing more.",
        why_concerns: ['irritation'],
        skin: skin, buckets: b, freq: freq, answers: answers,
        usesMakeup: usesMakeup
      });
    }

    // ---- PRIORITY 3: Map to cluster + skin + freq ---
    var result = buildRoutine(skin, b, freq, moist);
    result.skin = skin; result.buckets = b; result.freq = freq; result.answers = answers; result.usesMakeup = usesMakeup;
    // Barrier-first protocol: irritation + treatable concern. The user gets
    // the full active routine, but the PDP must surface a disclaimer telling
    // them to run BSC + moisturiser only for the first ~2 weeks before
    // layering in the actives. Flag is read by pdp.js (renderBarrierFirst).
    if (b.irritation) result.barrier_first = true;
    return finalize(result);
  }

  // =================================================================
  //  buildRoutine, applies persona rules from the Aditya sheet
  // =================================================================
  function buildRoutine(skin, b, freq, moist) {
    var isDry = (skin === 'dry');

    // Cluster priority: marks+acne > texture+acne > active_acne > marks > texture > maintenance
    var hasAcne = !!b.active_acne;
    var hasTexture = !!b.texture;
    var hasMarks = !!b.marks;
    var hasMaint = !!b.maintenance;

    // ---- MARKS + ACTIVE ACNE ----
    if (hasMarks && hasAcne) {
      return routineMarksAcne(skin, freq, moist, isDry);
    }
    // ---- TEXTURE + ACTIVE ACNE ----
    if (hasTexture && hasAcne) {
      return routineTextureAcne(skin, freq, moist, isDry);
    }
    // ---- ACTIVE ACNE only ----
    if (hasAcne) {
      return routineActiveAcne(skin, freq, moist, isDry);
    }
    // ---- MARKS only ----
    if (hasMarks) {
      return routineMarks(skin, moist);
    }
    // ---- TEXTURE only ----
    if (hasTexture) {
      return routineTexture(skin, moist, isDry);
    }
    // ---- MAINTENANCE ----
    if (hasMaint) {
      return routineMaintenance(skin, moist, isDry);
    }
    // Fallback, gentle default
    return {
      persona_id: 'DEFAULT-' + skin.toUpperCase(),
      products: ['BSC', moist, 'SS'],
      am: ['BSC', moist, 'SS'],
      pm: ['BSC', moist],
      active_count: 0,
      intro_delay: 'No actives, gentle daily care.',
      headline: 'A Gentle Routine for Your Skin',
      concern_summary: 'A simple, daily routine focused on barrier care, the foundation everything else builds on.',
      why_concerns: ['maintenance']
    };
  }

  // ---- Active Acne only ---------------------------------------------------
  function routineActiveAcne(skin, freq, moist, isDry) {
    var id, am, pm, products, activeCount = 1, intro, headlineFreq, summary;

    // Persistent or Frequent (almost always / every now and then), NCO-001 group
    if (freq === 'persistent' || freq === 'frequent') {
      if (isDry) {
        // DRY-001: RCF max 1x/day
        id = 'DRY-ACNE-PERSISTENT';
        products = ['RCF','BSC', moist, 'SS'];
        am = ['BSC', moist, 'SS'];
        pm = ['RCF', moist];
        intro = "Single active (RCF), used once a day at night. Dry skin needs a lighter touch with BPO.";
        headlineFreq = 'Persistent Acne';
        summary = "Dry acne-prone skin gets hit from two sides: breakouts and dryness. We're using RCF once at night to clear acne, BSC in the AM to keep your barrier intact, and BRIM to hydrate deeply.";
      } else {
        // NCO-001 / NCO-012 / NCO-023: RCF 2x/day, no BSC
        id = skin.toUpperCase() + '-ACNE-PERSISTENT';
        products = ['RCF', moist, 'SS'];
        am = ['RCF', moist, 'SS'];
        pm = ['RCF', moist];
        intro = "Single active (RCF), used twice daily. No separate cleanser needed, RCF handles AM and PM.";
        headlineFreq = 'Persistent Acne';
        summary = "You're breaking out almost all the time, so we're treating it daily and directly. RCF kills 93% of acne bacteria in 1 minute, twice a day. No fluff, no layers that aren't needed.";
      }
    }
    // Periodic (once a month), NCO-002 group
    else if (freq === 'periodic') {
      id = skin.toUpperCase() + '-ACNE-PERIODIC';
      products = isDry ? ['BSC','RCF', moist, 'SS'] : ['BSC','RCF', moist, 'SS'];
      am = ['BSC', moist, 'SS'];
      pm = ['RCF', moist];
      intro = "RCF at night (not AM), gentle AM, targeted PM.";
      headlineFreq = 'Periodic Breakouts';
      summary = "You break out on and off. We're using a gentle cleanser in the AM and RCF at night to target breakouts when your skin has downtime to heal.";
    }
    // Rare, NCO-003 group
    else {
      id = skin.toUpperCase() + '-ACNE-RARE';
      products = ['BSC','RCF', moist, 'SS'];
      am = ['BSC', moist, 'SS'];
      pm = ['BSC', moist];
      intro = "RCF is SOS only, use it as a spot treatment or replace your PM cleanse with RCF when a pimple appears. Otherwise, stick to BSC daily.";
      headlineFreq = 'Occasional Breakouts';
      summary = "You mostly stay clear. So we keep your daily routine gentle, and have RCF ready for when a pimple shows up.";
    }

    return {
      persona_id: id,
      products: products,
      am: am, pm: pm,
      active_count: activeCount,
      intro_delay: intro,
      headline: 'Your Routine for ' + headlineFreq + ' on ' + cap(skin) + ' Skin',
      concern_summary: summary,
      why_concerns: ['active_acne']
    };
  }

  // ---- Texture + Active Acne ----------------------------------------------
  function routineTextureAcne(skin, freq, moist, isDry) {
    var id, am, pm, products, activeCount, intro, summary;

    if (isDry) {
      // Dry skin can't do USDC daily. Fall back to BSC daily + RCF spot.
      id = 'DRY-TEXTURE-ACNE';
      products = ['BSC','RCF', moist, 'SS'];
      am = ['BSC', moist, 'SS'];
      pm = ['BSC', moist];
      activeCount = 1;
      intro = "On dry skin, USDC is too harsh, so we treat texture through barrier repair instead. RCF is used as a spot treatment when pimples appear, not as a full cleanser.";
      summary = "Dry skin with texture and breakouts is a delicate balance. Treating texture through hydration (not exfoliation) + targeted RCF for pimples keeps you clear without drying you out further.";
    }
    // Frequent (NCO-005), USDC AM + RCF PM
    else if (freq === 'frequent') {
      id = skin.toUpperCase() + '-TEXTURE-ACNE-FREQUENT';
      products = ['USDC','RCF', moist, 'SS'];
      am = ['USDC', moist, 'SS'];
      pm = ['RCF', moist];
      activeCount = 2;
      intro = "Two actives: USDC in the AM for texture, RCF at night for breakouts. Start USDC Week 1. Add RCF from Week 2 to let skin adjust first. Never use at the same time of day.";
      summary = "Blackheads and breakouts come from the same root, clogged pores. USDC clears pores by day, RCF kills bacteria by night. Alternating timing keeps skin balanced.";
    }
    // Persistent (NCO-006), RCF PM + BSC AM (no USDC daily; too harsh)
    else if (freq === 'persistent') {
      id = skin.toUpperCase() + '-TEXTURE-ACNE-PERSISTENT';
      products = ['BSC','RCF', moist, 'SS'];
      am = ['BSC', moist, 'SS'];
      pm = ['RCF', moist];
      activeCount = 1;
      intro = "Persistent acne is the priority. RCF at night clears breakouts; BSC in the AM keeps things gentle. Once breakouts calm (~Week 4–6), we can add USDC for texture.";
      summary = "With persistent acne AND texture, treating both aggressively at once overwhelms the skin. We're prioritising acne first, texture improves as inflammation calms.";
    }
    // Periodic (uses same as persistent, gentler base + RCF PM)
    else if (freq === 'periodic') {
      id = skin.toUpperCase() + '-TEXTURE-ACNE-PERIODIC';
      products = ['BSC','USDC','RCF', moist, 'SS'];
      am = ['USDC', moist, 'SS'];
      pm = ['BSC', moist]; // BSC most nights; RCF when breakout active
      activeCount = 2;
      intro = "USDC daily in the AM for texture. Use RCF at night on the 2–3 days you feel a breakout starting, otherwise BSC. Start USDC Week 1, introduce RCF from Week 2.";
      summary = "You get occasional acne alongside ongoing texture concerns. Daily USDC handles texture; RCF is ready for when breakouts hit.";
    }
    // Rare
    else {
      id = skin.toUpperCase() + '-TEXTURE-ACNE-RARE';
      products = ['USDC','BSC','RCF', moist, 'SS'];
      am = ['USDC', moist, 'SS'];
      pm = ['BSC', moist];
      activeCount = 1;
      intro = "USDC daily in the AM for texture. RCF is SOS only, spot treat when a pimple appears.";
      summary = "Texture is your main concern; acne is occasional. Daily USDC keeps pores clear; RCF steps in only when needed.";
    }

    return {
      persona_id: id,
      products: products,
      am: am, pm: pm,
      active_count: activeCount,
      intro_delay: intro,
      headline: 'Your Routine for Breakouts + Texture on ' + cap(skin) + ' Skin',
      concern_summary: summary,
      why_concerns: ['active_acne','texture']
    };
  }

  // ---- Marks + Active Acne ------------------------------------------------
  function routineMarksAcne(skin, freq, moist, isDry) {
    var id, am, pm, products, intro, summary;

    if (isDry) {
      // DRY-007, RCF SOS + SFARS daily
      id = 'DRY-MARKS-ACNE';
      products = ['SFARS','BSC','RCF', moist, 'SS'];
      am = ['BSC','SFARS', moist, 'SS'];
      pm = ['BSC','SFARS', moist];
      intro = "Two actives (RCF spot + SFARS daily). Start SFARS Week 1. Use RCF only as a spot treatment on individual pimples, never as a full face wash on dry skin.";
      summary = "You're dealing with both fresh breakouts and the marks left behind. SFARS fades marks daily; RCF is targeted to stop new breakouts from adding to the problem.";
    } else if (freq === 'persistent') {
      // NCO-001-like + SFARS (no dedicated sheet row, combine NCO-001 with SFARS added Wk2)
      id = skin.toUpperCase() + '-MARKS-ACNE-PERSISTENT';
      products = ['RCF','SFARS','BSC', moist, 'SS'];
      am = ['BSC','SFARS', moist, 'SS'];
      pm = ['RCF','SFARS', moist];
      intro = "Two actives (RCF + SFARS). Start RCF Week 1. Add SFARS from Week 2. Once stable, both can run together: RCF as cleanser, SFARS as serum after.";
      summary = "You're getting both active acne and marks, so we're solving two things at once. RCF clears what's coming; SFARS fades what's left.";
    } else if (freq === 'frequent' || freq === 'periodic') {
      // NCO-008, RCF 1x PM + SFARS
      id = skin.toUpperCase() + '-MARKS-ACNE-FREQUENT';
      products = ['RCF','SFARS','BSC', moist, 'SS'];
      am = ['BSC','SFARS', moist, 'SS'];
      pm = ['RCF','SFARS', moist];
      intro = "Two actives. Start RCF at night Week 1. Add SFARS from Week 2 at a different time, then slowly combine.";
      summary = "You break out periodically and have marks from old acne. SFARS daily fades marks while RCF at night keeps breakouts in check.";
    } else {
      // Rare, SFARS daily + RCF SOS
      id = skin.toUpperCase() + '-MARKS-ACNE-RARE';
      products = ['SFARS','BSC','RCF', moist, 'SS'];
      am = ['BSC','SFARS', moist, 'SS'];
      pm = ['BSC','SFARS', moist];
      intro = "SFARS daily for marks. RCF is SOS only, spot treat when a pimple shows up.";
      summary = "Your main focus is fading marks. SFARS does that daily. RCF waits in the wings for the rare breakout.";
    }

    return {
      persona_id: id,
      products: products,
      am: am, pm: pm,
      active_count: 2,
      intro_delay: intro,
      headline: 'Your Routine for Marks + Breakouts on ' + cap(skin) + ' Skin',
      concern_summary: summary,
      why_concerns: ['marks','active_acne']
    };
  }

  // ---- Marks only ---------------------------------------------------------
  function routineMarks(skin, moist) {
    return {
      persona_id: skin.toUpperCase() + '-MARKS',
      products: ['SFARS','BSC', moist, 'SS'],
      am: ['BSC','SFARS', moist, 'SS'],
      pm: ['BSC','SFARS', moist],
      active_count: 1,
      intro_delay: 'Single active (SFARS). Use every other day for Week 1, then daily.',
      headline: 'Your Routine for Post-Acne Marks on ' + cap(skin) + ' Skin',
      concern_summary: "Your main concern is the marks left behind after acne. Azelaic acid + niacinamide is the gentlest proven way to fade them, without triggering new irritation or photosensitivity.",
      why_concerns: ['marks']
    };
  }

  // ---- Texture only -------------------------------------------------------
  function routineTexture(skin, moist, isDry) {
    if (isDry) {
      // DRY-004, USDC alternate days + BSC other days
      return {
        persona_id: 'DRY-TEXTURE',
        products: ['USDC','BSC', moist, 'SS'],
        am: ['USDC', moist, 'SS'], // on USDC days
        pm: ['BSC', moist],
        active_count: 1,
        intro_delay: "Use USDC on alternate days only (Mon/Wed/Fri style). On other days, use BSC. Dry skin can't handle daily exfoliation, and this schedule gets you the texture benefit without irritation.",
        headline: 'Your Routine for Texture on Dry Skin',
        concern_summary: "Dry skin + texture is tricky, too much exfoliation destroys the barrier. USDC every other day, paired with deep hydration, gives you smooth skin without the dryness spiral.",
        why_concerns: ['texture']
      };
    }
    // NCO-004, USDC daily
    return {
      persona_id: skin.toUpperCase() + '-TEXTURE',
      products: ['USDC', moist, 'SS'],
      am: ['USDC', moist, 'SS'],
      pm: ['USDC', moist],
      active_count: 1,
      intro_delay: 'Single active (USDC). Use daily from Day 1, it\'s formulated for daily use.',
      headline: 'Your Routine for Blackheads & Texture on ' + cap(skin) + ' Skin',
      concern_summary: "Blackheads, whiteheads, and bumpy texture come from clogged pores. USDC clears them with dual acids (AHA + BHA), smoother skin in a few washes.",
      why_concerns: ['texture']
    };
  }

  // ---- Maintenance --------------------------------------------------------
  function routineMaintenance(skin, moist, isDry) {
    if (skin === 'oily') {
      return {
        persona_id: 'OILY-MAINTENANCE',
        products: ['USDC','BSC', moist, 'SS'],
        am: ['USDC', moist, 'SS'],
        pm: ['BSC', moist],
        active_count: 1,
        intro_delay: 'USDC daily as a low-grade maintenance active. Keeps pores clear so breakouts don\'t return.',
        headline: 'Your Maintenance Routine for Oily, Acne-Prone Skin',
        concern_summary: "You're in a good place, this keeps you there. USDC by day prevents pore congestion; BSC + barrier repair keeps your skin calm.",
        why_concerns: ['maintenance']
      };
    }
    return {
      persona_id: skin.toUpperCase() + '-MAINTENANCE',
      products: ['BSC', moist, 'SS'],
      am: ['BSC', moist, 'SS'],
      pm: ['BSC', moist],
      active_count: 0,
      intro_delay: 'No daily actives, just gentle, consistent barrier care.',
      headline: 'Your Maintenance Routine for ' + cap(skin) + ' Skin',
      concern_summary: "You're acne-prone but in a stable place. The best thing you can do is keep it gentle and consistent, no aggressive actives, no overloading.",
      why_concerns: ['maintenance']
    };
  }

  // =================================================================
  //  Helpers
  // =================================================================
  function finalize(r) {
    // Strip any out-of-stock products from the routine before anything else,
    // so they never appear as cards, steps, in the bundle price, in AI copy,
    // or in analytics. Temporary; controlled by OUT_OF_STOCK at the top.
    if (OUT_OF_STOCK.length) {
      var oos = function (c) { return OUT_OF_STOCK.indexOf(c) === -1; };
      r.products = (r.products || []).filter(oos);
      r.am = (r.am || []).filter(oos);
      r.pm = (r.pm || []).filter(oos);
    }

    // Apply EMC modifier (M1)
    if (r.answers && (r.answers.makeup === 'yes_daily' || r.answers.makeup === 'sometimes')) {
      if (r.products.indexOf('EMC') === -1) r.products.push('EMC');
      if (r.pm.indexOf('EMC') === -1) r.pm = ['EMC'].concat(r.pm);
    }

    // --- BSC reduction rule --------------------------------------------------
    // BSC is the only gentle face wash in the catalog, so it plays two roles:
    // the sensitive-skin cleanser, and the default gentle wash in routines whose
    // active is only used at one time of day. To suggest fewer products:
    //   bsc_state = 'bundled'  → BSC stays a recommended product (in cart + price).
    //                            Only for sensitive skin: Q2 "irritated/sensitive"
    //                            concern, OR Q6 sensitivity === 'very'.
    //   bsc_state = 'optional' → BSC dropped from the bundle, offered as an
    //                            optional add-on card (like EMC). Used when the
    //                            routine has no other daily cleanser, OR when
    //                            Q6 sensitivity === 'a_bit'.
    //   bsc_state = 'mention'  → BSC dropped from the bundle, but the AM/PM steps
    //                            kept their BSC slot; the renderer relabels it
    //                            "Gentle cleanser (Barrier Soothing Cleanser)".
    //   bsc_state = 'none'     → BSC not involved.
    // When dropped from the bundle, BSC is removed from r.products (so it isn't a
    // product card and isn't in the bundle price) but LEFT in r.am / r.pm so the
    // routine instructions stay intact (the renderer relabels the BSC step).
    var _a = r.answers || {};
    var _bk = r.buckets || {};
    var _hasBSC = r.products.indexOf('BSC') !== -1;
    var _sensStrong = !!_bk.irritation || _a.sensitivity === 'very';
    var _sensMild = _a.sensitivity === 'a_bit';
    if (_hasBSC && _sensStrong) {
      r.bsc_state = 'bundled';
    } else if (_hasBSC) {
      r.products = r.products.filter(function (c) { return c !== 'BSC'; });
      var _amPm = (r.am || []).concat(r.pm || []);
      var _otherWash = _amPm.indexOf('RCF') !== -1 || _amPm.indexOf('USDC') !== -1;
      r.bsc_optional = !_otherWash || _sensMild;
      r.bsc_state = r.bsc_optional ? 'optional' : 'mention';
    } else {
      // Routine never included BSC; only surface it optionally for sensitive skin.
      r.bsc_optional = _sensStrong || _sensMild;
      r.bsc_state = r.bsc_optional ? 'optional' : 'none';
    }

    // Canonical order
    r.products = orderProducts(dedupe(r.products));
    r.am = dedupe(r.am);
    r.pm = dedupe(r.pm);
    r.suggestions = []; // LPP removed from quiz per FQ9
    r.product_meta = r.products.map(function (p) { return Object.assign({ code: p }, PRODUCTS[p]); });
    r.suggestion_meta = [];
    return r;
  }

  function orderProducts(codes) {
    var order = ['EMC','RCF','USDC','BSC','SFARS','BRGM','BRIM','SS'];
    return codes.slice().sort(function (a,b) { return order.indexOf(a) - order.indexOf(b); });
  }

  function dedupe(arr) {
    var seen = {}, out = [];
    arr.forEach(function (v) { if (!seen[v]) { seen[v] = true; out.push(v); } });
    return out;
  }

  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }

  // =================================================================
  //  WHY templates (used by PDP renderer)
  //  Updated per Aditya sheet: glycolic unclogs pores, BPO leading, azelaic acid solubilised.
  // =================================================================
  var WHY = {
    // Engine-level fallback WHY. Per-product isolation rule: no cross-product mentions.
    RCF: {
      active_acne: "You deal with painful, inflamed breakouts. Acne happens when pores clog and C. acnes bacteria multiply beneath the surface. Rapid Clear Facewash uses patented Micronized Benzoyl Peroxide, particles small enough to reach deep into pores and kill 93% of acne bacteria in under 1 minute. Reduces acne and redness in the first week.",
      marks: "Rapid Clear Facewash clears breakouts at the source, which is where new marks form. Stopping the cycle here means fewer fresh marks later.",
      spot: "For the occasional pimple, RCF works on contact. Apply as a spot treatment and the Micronized BPO gets to work in minutes.",
      dry: "Dry skin can't handle daily BPO. We've set RCF as a once-a-day or spot treatment so you get acne-killing power without wrecking your barrier."
    },
    BSC: {
      daily: "Your routine uses a gentle cleanse as the daily base. Barrier Soothing Cleanser is 50% glycerin + ceramides + cica, it cleans without stripping the moisture barrier your skin needs to heal.",
      irritation: "When your skin is inflamed, even 'gentle' cleansers can sting. BSC is built to calm, no sulfates, no fragrance, just hydrating and soothing actives that reset your barrier.",
      pair: "Barrier Soothing Cleanser handles your gentler cleansing days. Non-stripping, pH-balanced, it keeps the barrier supported between treatment days."
    },
    USDC: {
      texture: "Blackheads, whiteheads, and uneven texture come from oil + dead skin blocking pores. USDC is a dual-acid cleanser: glycolic (AHA) exfoliates the surface while salicylic (BHA) dissolves oil inside the pore. Smoother, clearer skin in a few washes.",
      texture_dry: "On dry skin, daily exfoliation is too much, so we use USDC on alternate days only. You get the texture benefit without the barrier damage.",
      maintenance: "For oily, acne-prone skin in a good place, USDC keeps pores clear so breakouts don't return. Prevention over cure."
    },
    SFARS: {
      marks: "Post-acne marks come from melanin and inflammation damage. SpotFade uses 8% solubilized azelaic acid + 4% niacinamide, the most well-tolerated pigmentation fighter we could formulate. Fades dark marks without bleaching or triggering new irritation.",
      fungal_rosacea: "For fungal acne and rosacea, azelaic acid is the gold standard, it's both anti-inflammatory and anti-microbial. Unlike BPO or retinoids, it calms without flaring your skin.",
      active_acne: "SpotFade calms the inflammation that breakouts leave behind and fades marks before they set in. Solubilised azelaic acid + niacinamide, well-tolerated on sensitive, acne-prone skin."
    },
    BRGM: {
      default: "After active ingredients, your skin needs barrier repair. BRGM is lightweight and non-greasy, 1% ceramide complex + niacinamide + cica calm, hydrate, and strengthen without clogging oily/combo skin."
    },
    BRIM: {
      default: "Dry skin on an acne routine needs extra hydration. BRIM is rich but non-comedogenic, ceramides, panthenol, HA, and squalane repair your barrier and keep skin soft while actives do their work."
    },
    SS: {
      default: "Sun protection is non-negotiable on an acne routine. BPO and acids make skin photosensitive, and UV triggers PIH, the exact marks you're trying to fade. SPF 50+ with encapsulated hybrid filters: no white cast, no breakouts, full protection."
    },
    EMC: {
      default: "Makeup residue is a top cause of clogged pores in acne-prone skin. EMC melts everything, foundation, SPF, waterproof mascara, so your treatment actually reaches clean skin."
    }
  };

  // =================================================================
  //  JOBS (what the routine does), job-based plan section on the PDP.
  //  Each bucket → a "job" line. Rendered in the Plan section with a
  //  small coloured dot + label. "Universal" (barrier protection) is
  //  appended at the end unless the user's jobs already cover barrier.
  // =================================================================
  var JOBS = {
    active_acne: { id: 'clear_acne',  label: 'Clear the active breakouts',               dot: '#E57373' },
    texture:     { id: 'unclog',      label: 'Unclog and smooth your texture',            dot: '#E6A86B' },
    marks:       { id: 'fade_marks',  label: 'Fade the marks and even your tone',         dot: '#A88068' },
    irritation:  { id: 'calm',        label: 'Calm irritation and rebuild your barrier',  dot: '#6DA7C9' },
    fungal_acne: { id: 'fungal',      label: 'Target fungal acne without aggravating it', dot: '#9B80C9' },
    rosacea:     { id: 'rosacea',     label: 'Calm rosacea flare-ups and redness',        dot: '#E586A2' },
    maintenance: { id: 'maintain',    label: 'Keep acne-prone skin clear long-term',      dot: '#7DB5A6' },
    universal:   { id: 'barrier',     label: 'Protect your barrier while we treat',       dot: '#3EBCA2' }
  };

  // Return the ordered list of jobs for a persona. Rules:
  //  - Override personas (fungal/rosacea/irritation) show only their override job.
  //    Their job text already references barrier care, so no universal.
  //  - Otherwise show concern-jobs in order: active_acne, texture, marks
  //  - If no concern-jobs but maintenance, show maintenance
  //  - Append universal (barrier) at the end
  //  - Cap at 4 total
  function buildJobs(persona) {
    var b = (persona && persona.buckets) || {};
    if (b.fungal_acne) return [JOBS.fungal_acne];
    if (b.rosacea)     return [JOBS.rosacea];
    // Pure irritation override (no other treatable concern), single job.
    if (b.irritation && !(b.active_acne || b.texture || b.marks)) {
      return [JOBS.irritation];
    }

    var out = [];
    // Barrier-first cases: lead with the calm/rebuild job to set tone, then
    // the treatable concerns. The product list still serves both.
    if (b.irritation) out.push(JOBS.irritation);
    if (b.active_acne) out.push(JOBS.active_acne);
    if (b.texture)     out.push(JOBS.texture);
    if (b.marks)       out.push(JOBS.marks);
    if (!out.length && b.maintenance) out.push(JOBS.maintenance);

    out.push(JOBS.universal);
    return out.slice(0, 4);
  }

  // Group products into treatment / support / addon. Drives Section 3 layout.
  // Treatment = the actives that address the user's stated concerns.
  // Support = the cleansers/moisturiser/SPF that every routine needs.
  // Addon = optional cards (EMC is surfaced separately; nothing here for now).
  var PRODUCT_GROUP = {
    RCF:   'treatment',
    USDC:  'treatment',
    SFARS: 'treatment',
    BSC:   'support',
    BRGM:  'support',
    BRIM:  'support',
    SS:    'support',
    EMC:   'addon'
  };

  function groupProducts(persona) {
    var groups = { treatment: [], support: [], addon: [] };
    (persona.product_meta || []).forEach(function (m) {
      var g = PRODUCT_GROUP[m.code] || 'support';
      groups[g].push(m);
    });
    // Keep canonical order within each group (matches orderProducts)
    return groups;
  }

  // Which job does a given product primarily serve? Used for the small
  // "For clearing breakouts" label above each card. Returns a job object
  // or null (e.g. for the moisturiser — it quietly serves the barrier job).
  var PRODUCT_JOB = {
    RCF:   'active_acne',
    USDC:  'texture',
    SFARS: 'marks',          // also used for fungal/rosacea, overridden below
    BSC:   'universal',
    BRGM:  'universal',
    BRIM:  'universal',
    SS:    'universal',
    EMC:   'universal'
  };

  function jobForProduct(code, persona) {
    var b = (persona && persona.buckets) || {};
    // SFARS shifts depending on why it's prescribed
    if (code === 'SFARS') {
      if (b.fungal_acne) return JOBS.fungal_acne;
      if (b.rosacea)     return JOBS.rosacea;
      if (b.marks)       return JOBS.marks;
      if (b.active_acne) return JOBS.active_acne;
      return JOBS.marks;
    }
    var k = PRODUCT_JOB[code];
    return (k && JOBS[k]) || JOBS.universal;
  }

  // Expose
  window.TSSQuizEngine = {
    compute: compute,
    productMeta: PRODUCTS,
    concernMap: CONCERN_MAP,
    freqMap: FREQ_MAP,
    why: WHY,
    jobs: JOBS,
    buildJobs: buildJobs,
    groupProducts: groupProducts,
    jobForProduct: jobForProduct,
    version: 'v3-jobs'
  };
})();
